import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, FolderOpen, FolderUp, Loader2, Lock, Receipt, Unlock, X } from 'lucide-react';
import * as service from '../../../services/bauordnungService';
import type { SchreibKontext } from '../../../services/bauordnungService';
import { freigabeHindernisse, fortschritt } from '../bauordnungLogic';
import {
    GEBUCHTE_POSITION_SPALTE, GEBUCHT_AM_SPALTE, MENGE_JE_DOKUMENT, istGebucht, positionenFuer,
} from '../bauordnungBooking';
import type { BauordnungBuchungAnsicht } from '../useBauordnungBuchung';
import { BestandteilAbschlussHinweis } from '../../bestandteile/BestandteilAbschlussHinweis';
import type { AbschlussStand } from '../../bestandteile/useBestandteilAbschluss';
import { baueDokument, bauordnungDateiname } from '../dokument/baueDokument';
import { renderBauordnungPdf } from '../../../services/pdf/renderBauordnungPdf';
import {
    DOKUMENT_ID_SPALTE, ablageMeldung, erzeugteDokumente, offeneAblagen, type FreigabeAblageArgs,
} from '../ablage/bauordnungAblage';
import type { FreigabeFesthalten } from '../../../services/unterlagenAblage';
import { LAUF_SPERRGRUND, type FreigabeLaufAnsicht, type FreigabeSchluessel } from '../freigabeLauf';
import type { FreigabeAnstoss } from '../useFreigabeLauf';
import { DOKUMENTE, type Dokumentart } from '../kapitel';
import type { Bauordnung, BauordnungBundle } from '../types';
import type { ProjektMerkmale } from '../../planungsphase/types';
import type { Report, ReportMetadata, ServiceItem } from '../../../types';

/**
 * Ausgabe und Freigabe.
 *
 * Ein ENTWURF laesst sich ausdrucken — das muss er, sonst kann ihn niemand
 * gegenlesen. Dass es ein Entwurf ist, steht im Kopf des Dokuments, und die
 * offenen Punkte stehen ausdruecklich in den Kapiteln, in die sie gehoeren.
 *
 * Die Freigabe fragt `freigabeHindernisse()` — dieselbe Liste, die auch der
 * Dienst noch einmal prueft. Zwei Meinungen darueber, ob freigegeben werden
 * darf, waeren eine zu viel.
 *
 * ═══ FREIGABE UND ABLAGE SIND ZWEI SCHRITTE ═══════════════════════════════
 * Erst die Freigabe (ein Update, danach ist der Stand eingefroren), dann die
 * Ablage der PDFs in der Projektablage (`legeFreigabeAb`). Scheitert die
 * Ablage, bleibt die Freigabe gueltig — die Ansicht sagt, welches Dokument
 * fehlt, und bietet „Erneut ablegen". Begruendung in `ablage/bauordnungAblage.ts`.
 *
 * ═══ DER LAUF GEHOERT DEM MODUL ══════════════════════════════════════════
 * Freigabe, Journal und Ablage laufen im Modul (`useFreigabeLauf`), nicht
 * hier: wechselt man waehrend des Laufs den Reiter, wird dieser ausgehaengt
 * — Sperre und Ergebnis lagen frueher in SEINEM Zustand und waren beim
 * Zurueckkommen weg, obwohl der Lauf weiterging. Der Reiter liest `lauf` und
 * stoesst ueber `onFreigeben`, `onAblegen`, `onFesthalten` an.
 *
 * ═══ BUCHUNG NUR PER KLICK, JE DOKUMENT EINMAL ════════════════════════════
 * Nach der Freigabe bietet die Karte „Abrechnung" je erzeugtem Dokument den
 * Knopf „Leistung buchen" an (Spezifikation J) — und seit Plan B5 auch der
 * Abschlussdialog des Moduls DIREKT nach der Freigabe
 * (`BauordnungAbschlussDialog`). Nichts bucht von selbst — auch die Freigabe
 * nicht. Die Buchung selbst gehoert dem Modul (`useBauordnungBuchung`):
 * zuerst der Riegel in der Datenbank (`markiereGebucht`, Bedingung IM UPDATE),
 * bei `false` abbrechen, erst dann die Gutschrift ins Projekt
 * (`planeBauordnungBuchung`, rein) ueber `onUpdate` und die Zweitschrift ins
 * Buchungsjournal (`queueJournalMirror`, Quelle 'bauordnung'). Ohne
 * `report`/`onUpdate` — der Container hat den Leistungsstand nicht erhalten —
 * gibt es den Knopf nicht, nur den Satz dazu.
 */

interface Props {
    bundle: BauordnungBundle;
    merkmale: ProjektMerkmale;
    ctx: SchreibKontext;
    projektName: string;
    projektNummer?: string;
    projektAdresse?: string;
    schreiben: <T>(vorgang: () => Promise<T>) => Promise<T | null>;
    aufReiter: (reiter: string) => void;
    /** Oeffnet die Projektablage, gefiltert auf dieses Modul. */
    aufDokumente?: () => void;
    /** Das Projekt mit Leistungsstand (`metadata.globalData.services`) — fuer die Buchung. */
    report?: Report | null;
    /** Schreibt den gebuchten Leistungsstand ins Projekt (App.handleUpdateReport). */
    onUpdate?: (report: Report) => void;
    /** Die Leistungsbuchung des Moduls (`useBauordnungBuchung`) — Lauf, Meldung, Knopf. */
    buchung: BauordnungBuchungAnsicht;
    /**
     * Stammdaten fuer den Planungsstand der Freigabe (Stufe 6.1) — vom Modul
     * EINMAL gelesen. `undefined`: nicht mitgegeben, der Helfer liest selbst;
     * `null`: es gibt keine — dann wird kein Anker gebildet.
     */
    metadata?: Partial<ReportMetadata> | null;
    /** `true`, solange das Modul die Stammdaten noch liest — die Freigabe wartet darauf. */
    stammdatenLaden?: boolean;
    /**
     * Der Lauf nach der Freigabe (Journal + Ablage), wie das Modul ihn haelt —
     * Sperre und letztes Ergebnis, schon auf die aktuelle Freigabe zugeschnitten.
     */
    lauf: FreigabeLaufAnsicht;
    /** Freigeben, danach Journal und Ablage — der Ablauf gehoert dem Modul. */
    onFreigeben: (anstoss: FreigabeAnstoss) => void;
    /** „Erneut ablegen". */
    onAblegen: (args: FreigabeAblageArgs) => void;
    /** „Erneut festhalten" mit dem Auftrag des ersten Versuchs. */
    onFesthalten: (fuer: FreigabeSchluessel, auftrag: FreigabeFesthalten) => void;
    /** Nach „Neue Fassung eroeffnen": der Aenderungsdienst soll neu lesen. */
    onAbgleichNoetig?: () => void;
}

type Ton = 'ok' | 'teils' | 'fehler';

const MELDUNG_FARBE: Record<Ton, string> = {
    ok: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    teils: 'bg-amber-50 border-amber-200 text-amber-900',
    fehler: 'bg-rose-50 border-rose-200 text-rose-800',
};

const tagDeutsch = (iso?: string | null): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

/**
 * Die Abrechnungszeile EINES Dokuments: gebucht · buchbar · keine Position.
 * Zeigt an und ruft auf; welche Position in Frage kommt, entscheidet
 * `positionenFuer` (J.3) — eine Paketposition nie (Plan Kernregel 7).
 * Im Reiter UND im Abschlussdialog dieselbe Zeile; ein schon gebuchtes
 * Dokument sagt es („gebucht am …"), bevor jemand ein zweites Mal klickt.
 */
export const AbrechnungZeile: React.FC<{
    o: Bauordnung;
    art: Dokumentart;
    services: ServiceItem[];
    darfBuchen: boolean;
    laeuft: boolean;
    onBuchen: (art: Dokumentart, serviceId: string) => void;
}> = ({ o, art, services, darfBuchen, laeuft, onBuchen }) => {
    const auswahl = useMemo(() => positionenFuer(services, art), [services, art]);
    const [gewaehlt, setGewaehlt] = useState('');
    const titel = DOKUMENTE[art].titel;

    if (istGebucht(o, art)) {
        const position = services.find(s => s.id === o[GEBUCHTE_POSITION_SPALTE[art]]);
        return (
            <li className="text-[12px] flex items-start gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                <span>
                    <span className="font-semibold text-slate-700">{titel}</span>
                    <span className="text-emerald-800"> · gebucht am {tagDeutsch(o[GEBUCHT_AM_SPALTE[art]])}</span>
                    {position && <span className="text-slate-500"> auf {position.lvPosition} {position.title}</span>}
                </span>
            </li>
        );
    }

    // J.3: Katalogkennung zuerst; sonst waehlt der Anwender einmal unter den
    // `site_rules`-Positionen; sonst wird nicht gebucht und nicht gescheitert.
    const serviceId = auswahl.vorgeschlagen?.id ?? gewaehlt;
    if (!auswahl.vorgeschlagen && auswahl.auswahl.length === 0) {
        return (
            <li className="text-[12px] flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                <span>
                    <span className="font-semibold text-slate-700">{titel}</span>
                    <span className="text-slate-500"> · keine LV-Position für dieses Dokument gefunden, es wird nichts gebucht.</span>
                </span>
            </li>
        );
    }

    return (
        <li className="text-[12px] flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-700">{titel}</span>
            {auswahl.vorgeschlagen ? (
                <span className="text-slate-500">
                    → {auswahl.vorgeschlagen.lvPosition} {auswahl.vorgeschlagen.title}
                </span>
            ) : (
                <select
                    value={gewaehlt}
                    onChange={e => setGewaehlt(e.target.value)}
                    disabled={!darfBuchen || laeuft}
                    className="text-[12px] border border-slate-200 rounded-md px-2 py-1 bg-white"
                >
                    <option value="">LV-Position wählen …</option>
                    {auswahl.auswahl.map(s => (
                        <option key={s.id} value={s.id}>{s.lvPosition} {s.title}</option>
                    ))}
                </select>
            )}
            <button
                type="button"
                onClick={() => onBuchen(art, serviceId)}
                disabled={!darfBuchen || laeuft || !serviceId}
                title={darfBuchen
                    ? `${MENGE_JE_DOKUMENT} Pauschal auf die gewählte Position buchen, genau einmal`
                    : 'Die Buchung ist hier nicht verfügbar.'}
                className="text-[12px] font-bold text-emerald-800 border border-emerald-300 rounded-lg px-3 py-1.5 flex items-center gap-1.5 hover:bg-emerald-100 disabled:opacity-50"
            >
                {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
                Leistung buchen
            </button>
        </li>
    );
};

export const AusgabeReiter: React.FC<Props> = ({
    bundle, merkmale, ctx, projektName, projektNummer, projektAdresse, schreiben, aufReiter, aufDokumente,
    report = null, onUpdate, buchung, metadata, stammdatenLaden = false, lauf, onFreigeben, onAblegen, onFesthalten,
    onAbgleichNoetig,
}) => {
    const o = bundle.ordnung;
    const [fassung, setFassung] = useState('');
    /**
     * `lauf.laeuft`: von der Freigabe bis Journal UND Ablage durch sind — in
     * dieser Zeit sind Freigeben, „Neue Fassung eroeffnen", „Erneut ablegen"
     * und „Erneut festhalten" gesperrt, auch nach einem Reiterwechsel: eine
     * zwischendurch eroeffnete Fassung machte den Kopfsatz, aus dem gerade
     * abgelegt wird, wieder zum Entwurf.
     * `lauf.journal`: der Planungsstand dieser Freigabe konnte nicht
     * festgehalten werden (Stufe 6.1) — die Freigabe gilt, der Satz steht an
     * ihr, bis die naechste ihn ersetzt.
     */
    const { laeuft } = lauf;
    const freigabeJournal = lauf.journal;
    const buchungLaeuft = buchung.laeuft;
    const buchungsMeldung = buchung.meldung;
    const hindernisse = useMemo(() => freigabeHindernisse(bundle, merkmale), [bundle, merkmale]);

    if (!o) return null;
    const gesperrt = o.status === 'freigegeben';

    const dokumente = erzeugteDokumente(o);
    const offene = offeneAblagen(o);

    const globalData = report?.metadata?.globalData ?? null;
    const services = globalData?.services ?? [];
    const darfBuchen = buchung.darfBuchen;

    /**
     * Bucht EIN Dokument — ueber die Buchung des Moduls (`useBauordnungBuchung`):
     * Riegel, Gutschrift ueber `onUpdate`, Zweitschrift ins Buchungsjournal
     * (Quelle 'bauordnung', Muster B). Die fruehere Luecke („`JournalSource`
     * kennt keinen Wert fuer dieses Modul") ist damit geschlossen.
     */
    const buche = (art: Dokumentart, serviceId: string) => {
        if (!darfBuchen || buchungLaeuft) return;
        void buchung.buche(o, art, serviceId);
    };

    const exportiere = (art: Dokumentart) => {
        const dok = baueDokument(bundle, art, {
            projektName, projektNummer, projektAdresse, merkmale,
        });
        const pdf = renderBauordnungPdf(dok, { fusszeileLinks: o.erlassender_firma });
        pdf.save(bauordnungDateiname(art, projektName || 'Projekt', o.stand));
    };

    /** Ablage-Argumente aus dem uebergebenen Buendel — „Erneut ablegen" und Freigabe gleich. */
    const ablageArgs = (b: BauordnungBundle): FreigabeAblageArgs => ({
        bundle: b, merkmale, projektName, projektNummer, projektAdresse, ctx,
    });

    const freigeben = () => onFreigeben({ ...ablageArgs(bundle), fassung, metadata });

    /** Sperrgrund fuer „Freigeben" — sichtbar, kein stummer Knopf. */
    const freigabeSperre = stammdatenLaden
        ? 'Die Stammdaten des Projekts werden noch gelesen. Ohne sie kann der Planungsstand der Freigabe nicht festgehalten werden.'
        : null;

    const meldung = lauf.ablage ? ablageMeldung(lauf.ablage) : null;

    return (
        <div className="space-y-6 max-w-3xl">
            <section>
                <h3 className="text-sm font-bold text-slate-800 mb-2">Ausgabe</h3>
                {!dokumente.length && (
                    <p className="text-[12px] text-amber-600">
                        Es ist kein Dokument zur Erzeugung ausgewählt. Das steht unter „Grundlagen".
                    </p>
                )}
                <div className="space-y-2">
                    {dokumente.map(art => {
                        const f = fortschritt(bundle, art, merkmale);
                        return (
                            <div key={art} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4">
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-bold text-slate-800">{DOKUMENTE[art].titel}</p>
                                    <p className="text-[12px] text-slate-500">{DOKUMENTE[art].untertitel}</p>
                                    <div className="mt-2 flex items-center gap-2">
                                        <div className="h-1.5 bg-slate-100 rounded-full flex-1 max-w-[220px] overflow-hidden">
                                            <div className="h-full bg-fuchsia-500 rounded-full transition-all"
                                                style={{ width: `${f.prozent}%` }} />
                                        </div>
                                        <span className="text-[11px] text-slate-400 tabular-nums">
                                            {f.fertig} von {f.gesamt} Kapiteln
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => exportiere(art)}
                                    className="bg-slate-800 text-white text-sm font-bold rounded-lg px-4 py-2 flex items-center gap-1.5 hover:bg-slate-900 shrink-0"
                                >
                                    <Download className="w-4 h-4" /> PDF
                                </button>
                            </div>
                        );
                    })}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                    Ein Entwurf lässt sich drucken, sonst kann ihn niemand gegenlesen. Er trägt dann
                    einen Entwurfsvermerk auf Seite eins.
                </p>
            </section>

            <section>
                <h3 className="text-sm font-bold text-slate-800 mb-2">Freigabe</h3>

                {meldung && (
                    <div className={`border rounded-xl px-3 py-2 mb-3 text-[12px] leading-relaxed ${MELDUNG_FARBE[meldung.ton]}`}>
                        {meldung.text}
                    </div>
                )}
                {/* Das Freigabe-Journal (Stufe 6.1) konnte nicht geschrieben werden:
                    die Freigabe gilt, die Fassung wird aber nicht auf Aenderungen ueberwacht. */}
                {freigabeJournal && (
                    <div className={`border rounded-xl px-3 py-2 mb-3 text-[12px] leading-relaxed flex flex-wrap items-center gap-2 ${MELDUNG_FARBE.teils}`}>
                        <span className="min-w-0 flex-1">{freigabeJournal.hinweis}</span>
                        {freigabeJournal.wiederholung && !ctx.nurAnsicht && (
                            <button
                                type="button"
                                disabled={laeuft}
                                title={laeuft ? LAUF_SPERRGRUND : undefined}
                                onClick={() => { const w = freigabeJournal.wiederholung; if (w) onFesthalten(freigabeJournal.fuer, w); }}
                                className="text-[11px] font-bold border border-amber-300 bg-white rounded-lg px-2.5 py-1 hover:bg-amber-100 disabled:opacity-50"
                            >
                                {lauf.phase === 'journal' ? 'Wird festgehalten …' : 'Erneut festhalten'}
                            </button>
                        )}
                    </div>
                )}

                {gesperrt ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
                        <p className="text-sm font-bold text-emerald-800 flex items-center gap-2">
                            <Lock className="w-4 h-4" /> Freigegeben
                            {o.freigegeben_am && ` am ${o.freigegeben_am.slice(0, 10)}`}
                            {o.freigegeben_von_name && ` durch ${o.freigegeben_von_name}`}
                        </p>
                        <p className="text-[12px] text-emerald-900/80 mt-1 leading-relaxed">
                            Der Stand ist eingefroren: die gezogenen Kapitel sind festgehalten. Eine spätere
                            Planverschiebung oder ein neues firmenweites Kapitel ändern das gedruckte Dokument
                            nicht mehr.
                        </p>

                        {/* Der Ablagestand je Dokument. Er liest die Ids am Kopfsatz —
                            die Freigabe leert sie, also zeigt „in der Projektablage" nie
                            auf eine aeltere Fassung. */}
                        {dokumente.length > 0 && (
                            <div className="mt-3 bg-white/70 border border-emerald-200 rounded-xl p-3">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-emerald-800/70 mb-1.5">
                                    Projektablage
                                </p>
                                <ul className="space-y-1">
                                    {dokumente.map(art => {
                                        const abgelegt = Boolean(o[DOKUMENT_ID_SPALTE[art]]);
                                        return (
                                            <li key={art} className="text-[12px] flex items-center gap-1.5">
                                                {abgelegt
                                                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                                    : <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                                                <span className="font-semibold text-slate-700">{DOKUMENTE[art].titel}</span>
                                                <span className={abgelegt ? 'text-emerald-800' : 'text-amber-800'}>
                                                    {abgelegt ? '· in der Projektablage' : '· nicht abgelegt'}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <div className="flex flex-wrap items-center gap-2 mt-2.5">
                                    {offene.length > 0 && !ctx.nurAnsicht && (
                                        <button
                                            onClick={() => onAblegen(ablageArgs(bundle))}
                                            disabled={laeuft}
                                            title={laeuft ? LAUF_SPERRGRUND : undefined}
                                            className="text-[12px] font-bold text-amber-900 border border-amber-300 bg-amber-50 rounded-lg px-3 py-1.5 flex items-center gap-1.5 hover:bg-amber-100 disabled:opacity-50"
                                        >
                                            {laeuft
                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                : <FolderUp className="w-3.5 h-3.5" />}
                                            Erneut ablegen
                                        </button>
                                    )}
                                    {aufDokumente && (
                                        <button
                                            onClick={aufDokumente}
                                            className="text-[12px] font-bold text-emerald-800 border border-emerald-300 rounded-lg px-3 py-1.5 flex items-center gap-1.5 hover:bg-emerald-100"
                                        >
                                            <FolderOpen className="w-3.5 h-3.5" /> Ablage öffnen
                                        </button>
                                    )}
                                </div>
                                {laeuft ? (
                                    <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                                        {lauf.phase === 'journal'
                                            ? 'Der Planungsstand der Freigabe wird gerade festgehalten…'
                                            : 'Die PDFs des eingefrorenen Stands werden gerade in der Projektablage abgelegt…'}
                                    </p>
                                ) : offene.length > 0 && (
                                    <p className="text-[11px] text-amber-900/80 mt-2 leading-relaxed">
                                        Die Freigabe gilt unabhängig von der Ablage. „Erneut ablegen" erzeugt das PDF
                                        aus dem eingefrorenen Stand und legt nur nach, was fehlt.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Abrechnung: je Dokument genau einmal, nur per Klick (J). */}
                        {dokumente.length > 0 && (
                            <div className="mt-3 bg-white/70 border border-emerald-200 rounded-xl p-3">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-emerald-800/70 mb-1.5">
                                    Abrechnung
                                </p>
                                {buchungsMeldung && (
                                    <div className={`border rounded-lg px-3 py-1.5 mb-2 text-[12px] leading-relaxed ${MELDUNG_FARBE[buchungsMeldung.ton]}`}>
                                        {buchungsMeldung.text}
                                    </div>
                                )}
                                <ul className="space-y-1.5">
                                    {dokumente.map(art => (
                                        <AbrechnungZeile
                                            key={art}
                                            o={o}
                                            art={art}
                                            services={services}
                                            darfBuchen={darfBuchen}
                                            laeuft={buchungLaeuft === art}
                                            onBuchen={(a, id) => buche(a, id)}
                                        />
                                    ))}
                                </ul>
                                {!report || !onUpdate ? (
                                    <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                                        Die Leistungsbuchung ist hier nicht verfügbar: dem Modul wurde der Leistungsstand
                                        des Projekts nicht übergeben.
                                    </p>
                                ) : (
                                    <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                                        Gebucht wird die Freigabe (nicht der Druck), je Dokument genau einmal. Eine neue
                                        Fassung desselben Dokuments ist keine zweite Leistung.
                                    </p>
                                )}
                            </div>
                        )}

                        {!ctx.nurAnsicht && (
                            <button
                                onClick={() => { void schreiben(() => service.revisionEroeffnen(o, ctx)).then(p => { if (p !== null) onAbgleichNoetig?.(); }); }}
                                disabled={laeuft}
                                title={laeuft ? LAUF_SPERRGRUND : undefined}
                                className="mt-3 text-[12px] font-bold text-emerald-800 border border-emerald-300 rounded-lg px-3 py-1.5 flex items-center gap-1.5 hover:bg-emerald-100 disabled:opacity-50"
                            >
                                <Unlock className="w-3.5 h-3.5" /> Neue Fassung eröffnen
                            </button>
                        )}
                    </div>
                ) : hindernisse.length ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                        <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            {hindernisse.length} {hindernisse.length === 1 ? 'Punkt steht' : 'Punkte stehen'} der Freigabe entgegen
                        </p>
                        <ul className="mt-2 space-y-1">
                            {hindernisse.slice(0, 12).map((h, i) => (
                                <li key={i}>
                                    <button
                                        onClick={() => aufReiter(h.bereich)}
                                        className="text-[12px] text-amber-900/90 hover:text-amber-900 hover:underline text-left"
                                    >
                                        • {h.text}
                                    </button>
                                </li>
                            ))}
                            {hindernisse.length > 12 && (
                                <li className="text-[12px] text-amber-800">
                                    … und {hindernisse.length - 12} weitere.
                                </li>
                            )}
                        </ul>
                    </div>
                ) : (
                    <div className="bg-white border border-slate-200 rounded-2xl p-4">
                        <p className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Die Freigabe ist möglich.
                        </p>
                        <p className="text-[12px] text-slate-500 mt-1">
                            Mit der Freigabe wird der Stand eingefroren. Die gezogenen Kapitel werden
                            festgehalten, damit das unterschriebene Dokument nicht nachträglich wächst.
                            Die freigegebenen PDFs landen danach automatisch in der Projektablage.
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                            <input
                                value={fassung} onChange={e => setFassung(e.target.value)}
                                placeholder="Fassungsbezeichnung, z. B. Rev. 01"
                                className="text-sm border border-slate-200 rounded-lg px-3 py-2 flex-1"
                            />
                            <button
                                onClick={freigeben}
                                disabled={ctx.nurAnsicht || laeuft || !!freigabeSperre}
                                title={freigabeSperre || (laeuft ? LAUF_SPERRGRUND : undefined)}
                                className="bg-emerald-600 text-white text-sm font-bold rounded-lg px-4 py-2 flex items-center gap-1.5 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400"
                            >
                                <Lock className="w-4 h-4" /> Freigeben
                            </button>
                        </div>
                        {freigabeSperre && (
                            <p className="text-[11px] text-amber-800 mt-2 leading-relaxed">{freigabeSperre}</p>
                        )}
                    </div>
                )}
            </section>

            {o.historie.length > 0 && (
                <section>
                    <h3 className="text-sm font-bold text-slate-800 mb-2">Verlauf</h3>
                    <ul className="space-y-1">
                        {[...o.historie].reverse().map((h, i) => (
                            <li key={i} className="text-[12px] text-slate-500">
                                <span className="tabular-nums text-slate-400">{(h.am || '').slice(0, 10)}</span>
                                {' · '}{h.was}{h.wer ? ` (${h.wer})` : ''}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

/**
 * DER ABSCHLUSSSCHRITT NACH DER FREIGABE (Plan Paketposition B5).
 *
 * Bis hierhin stand die Buchung nur in der Karte „Abrechnung" des Reiters —
 * gefunden hat sie, wer danach suchte. Jetzt oeffnet das Modul diesen Dialog
 * DIREKT nach der Freigabe (Fall 1/3: es gibt eine eigene Position fuer ein
 * erzeugtes Dokument). Er zeigt dieselben Zeilen wie die Karte — je Dokument
 * genau einmal, ein schon gebuchtes sagt es — und darunter, was die Freigabe
 * an Bestandteilen einer Paketposition erledigt hat (Fall 3). Keine Preise.
 */
export const BauordnungAbschlussDialog: React.FC<{
    o: Bauordnung;
    services: ServiceItem[];
    buchung: BauordnungBuchungAnsicht;
    bestandteile: AbschlussStand | null;
    onSchliessen: () => void;
}> = ({ o, services, buchung, bestandteile, onSchliessen }) => {
    const dokumente = erzeugteDokumente(o);
    return (
        <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-scale-in">
                <div className="bg-slate-900 text-white p-5 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-emerald-400" /> Freigegeben: Leistung buchen
                    </h3>
                    <button onClick={onSchliessen} className="hover:bg-white/10 p-1 rounded transition-colors" title="Schließen">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-sm text-slate-600">
                        Die Fassung ist freigegeben. Gebucht wird die Freigabe (nicht der Druck), je Dokument genau einmal;
                        eine neue Fassung desselben Dokuments ist keine zweite Leistung.
                    </p>
                    {buchung.meldung && (
                        <div className={`border rounded-lg px-3 py-1.5 text-[12px] leading-relaxed ${MELDUNG_FARBE[buchung.meldung.ton]}`}>
                            {buchung.meldung.text}
                        </div>
                    )}
                    <ul className="space-y-1.5">
                        {dokumente.map(art => (
                            <AbrechnungZeile
                                key={art}
                                o={o}
                                art={art}
                                services={services}
                                darfBuchen={buchung.darfBuchen}
                                laeuft={buchung.laeuft === art}
                                onBuchen={(a, id) => { if (buchung.darfBuchen && !buchung.laeuft) void buchung.buche(o, a, id); }}
                            />
                        ))}
                    </ul>
                    <BestandteilAbschlussHinweis stand={bestandteile} />
                </div>
                <div className="p-4 bg-slate-50 border-t flex justify-end">
                    <button
                        type="button"
                        onClick={onSchliessen}
                        disabled={!!buchung.laeuft}
                        className="px-4 py-2 text-sm font-bold text-white bg-slate-800 hover:bg-slate-900 rounded-lg disabled:opacity-50"
                    >
                        Fertig
                    </button>
                </div>
            </div>
        </div>
    );
};
